/**
 * Job Description detail page.
 * Route: /ats/jobs/:jobId/job-descriptions/:jdId
 *
 * Shows full JD metadata, raw content (editable), and AI-parsed data.
 */

import { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  FileText,
  Edit2,
  ExternalLink,
  Save,
  X,
  Loader2,
  AlertCircle,
  Briefcase,
  MapPin,
  DollarSign,
  Building2,
  Tag,
  CheckCircle,
  Trash2,
  RefreshCw,
  Upload,
} from 'lucide-react';
import {
  Alert,
  AlertDescription,
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  useAuth,
  toast,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@aaas/shared';
import {
  useJDDetailByIdQuery,
  useJDFileUrlQuery,
  useUpdateJDMutation,
  useDeleteJDMutation,
  useReparseMutation,
  useUploadJDFileMutation,
} from '../../hooks/useJobDescriptionQueries';
import { PDFViewer } from '../../components/PDFViewer';
import { useATSRole } from '../../hooks/useATSRole';
import {
  type ParsedJDData,
  type ParsedDataForm,
  emptyForm,
  parsedToForm,
  formToParsed,
  formatFileSize,
  extractFilename,
  isPDF,
} from '../../utils/jd-form';
import { ProcessingStatusBadge } from '../../components/jd/ProcessingStatusBadge';
import { ParsedSection, TagList, BulletList } from '../../components/jd/ParsedDataDisplay';

// --- Main page ---

export function JDDetailPage() {
  const { t } = useTranslation('ats');
  const { jdId } = useParams<{ jdId: string }>();
  const navigate = useNavigate();
  const { user, currentWorkspace, idToken } = useAuth();
  const { isLeaderOrAbove } = useATSRole();

  // Inline name editing
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState('');
  const nameInputRef = useRef<HTMLInputElement>(null);

  // File upload (replace)
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Active tab
  const [activeTab, setActiveTab] = useState<'parsed' | 'preview'>('parsed');

  // File URL editing (replace mode)
  const [editingFileUrl, setEditingFileUrl] = useState(false);

  // Raw content editing
  const [editingRawContent, setEditingRawContent] = useState(false);
  const [rawContentValue, setRawContentValue] = useState('');

  // Parsed data editing (structured form)
  const [editingParsedData, setEditingParsedData] = useState(false);
  const [parsedDataForm, setParsedDataForm] = useState<ParsedDataForm>(emptyForm);

  // Delete dialog
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const isEditing = editingParsedData || editingName || editingFileUrl || editingRawContent;

  const { data: jd, isLoading, error } = useJDDetailByIdQuery({
    workspaceId: currentWorkspace?.id,
    idToken: idToken || undefined,
    jdId,
    enabled: !!user && !!currentWorkspace && !!jdId,
    pausePolling: isEditing,
  });

  const jobId = jd?.job_id;

  const updateMutation = useUpdateJDMutation({
    workspaceId: currentWorkspace?.id ?? '',
    idToken: idToken ?? '',
    jobId: jobId ?? '',
  });

  const deleteMutation = useDeleteJDMutation({
    workspaceId: currentWorkspace?.id ?? '',
    idToken: idToken ?? '',
    jobId: jobId ?? '',
  });

  const reparseMutation = useReparseMutation({
    workspaceId: currentWorkspace?.id ?? '',
    idToken: idToken ?? '',
    jobId: jobId ?? '',
  });

  const uploadFileMutation = useUploadJDFileMutation({
    workspaceId: currentWorkspace?.id ?? '',
    idToken: idToken ?? '',
    jobId: jobId ?? '',
  });

  const fileUrlQuery = useJDFileUrlQuery({
    workspaceId: currentWorkspace?.id,
    idToken: idToken || undefined,
    jobId,
    jdId,
    enabled: activeTab === 'preview',
  });

  useEffect(() => {
    if (editingName) nameInputRef.current?.focus();
  }, [editingName]);

  const handleStartNameEdit = () => {
    setNameValue(jd?.name ?? '');
    setEditingName(true);
  };

  const handleSaveName = async () => {
    if (!nameValue.trim() || !jdId) return;
    try {
      await updateMutation.mutateAsync({ jdId, data: { name: nameValue.trim() } });
      setEditingName(false);
      toast.success(t('jd.detail.toast.nameUpdated'));
    } catch {
      toast.error(t('jd.detail.toast.nameUpdateFailed'));
    }
  };

  const handleCancelNameEdit = () => {
    setEditingName(false);
    setNameValue('');
  };

  const handleStartFileUrlEdit = () => {
    setEditingFileUrl(true);
  };

  const handleStartParsedDataEdit = () => {
    setParsedDataForm(parsedToForm(jd?.parsed_data as ParsedJDData | null));
    setEditingParsedData(true);
  };

  const handleSaveParsedData = async () => {
    if (!jdId) return;
    try {
      await updateMutation.mutateAsync({ jdId, data: { parsed_data: formToParsed(parsedDataForm) } });
      setEditingParsedData(false);
      toast.success(t('jd.detail.toast.parsedUpdated'));
    } catch {
      toast.error(t('jd.detail.toast.parsedUpdateFailed'));
    }
  };

  const handleCancelParsedDataEdit = () => {
    setEditingParsedData(false);
    setParsedDataForm(emptyForm());
  };

  const handleSaveNewFile = async () => {
    if (!pendingFile || !jdId) return;
    try {
      await uploadFileMutation.mutateAsync({ jdId, file: pendingFile });
      setPendingFile(null);
      setEditingFileUrl(false);
      toast.success(t('jd.detail.toast.fileReplaced'));
    } catch {
      toast.error(t('jd.detail.toast.fileUploadFailed'));
    }
  };

  const handleCancelFileEdit = () => {
    setEditingFileUrl(false);
    setPendingFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleStartRawContentEdit = () => {
    setRawContentValue(jd?.raw_content ?? '');
    setEditingRawContent(true);
  };

  const handleSaveRawContent = async () => {
    if (!jdId) return;
    try {
      await updateMutation.mutateAsync({ jdId, data: { raw_content: rawContentValue } });
      setEditingRawContent(false);
      toast.success(t('jd.detail.toast.contentUpdated'));
    } catch {
      toast.error(t('jd.detail.toast.contentUpdateFailed'));
    }
  };

  const handleCancelRawContentEdit = () => {
    setEditingRawContent(false);
    setRawContentValue('');
  };

  const setField = (key: keyof ParsedDataForm, value: string | boolean) =>
    setParsedDataForm(prev => ({ ...prev, [key]: value }));

  const handleReparse = async () => {
    if (!jdId) return;
    try {
      await reparseMutation.mutateAsync(jdId);
      toast.success(t('jd.detail.toast.reparseStarted'));
    } catch {
      toast.error(t('jd.detail.toast.reparseFailed'));
    }
  };

  const handleDeleteConfirm = async () => {
    if (!jdId) return;
    try {
      await deleteMutation.mutateAsync(jdId);
      navigate('/ats/job-descriptions');
    } catch {
      toast.error(t('jd.detail.toast.deleteFailed'));
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600" />
      </div>
    );
  }

  if (error || !jd || !jobId) {
    return (
      <div className="space-y-4">
        <button
          onClick={() => navigate('/ats/job-descriptions')}
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('jd.detail.backShort')}
        </button>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{t('jd.detail.loadFailed')}</AlertDescription>
        </Alert>
      </div>
    );
  }

  const parsed = jd.parsed_data as ParsedJDData | null;
  const hasParseError = parsed && 'error' in parsed;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <button
            onClick={() => navigate('/ats/job-descriptions')}
            className="mt-1 p-1.5 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
            aria-label={t('jd.detail.backAria')}
          >
            <ArrowLeft className="h-5 w-5" />
          </button>

          <div className="space-y-1.5">
            {/* Editable name */}
            {editingName ? (
              <div className="flex items-center gap-2">
                <input
                  ref={nameInputRef}
                  value={nameValue}
                  onChange={(e) => setNameValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSaveName();
                    if (e.key === 'Escape') handleCancelNameEdit();
                  }}
                  className="text-2xl font-bold text-gray-900 border-b-2 border-orange-500 focus:outline-none bg-transparent min-w-0 w-72"
                />
                <button
                  onClick={handleSaveName}
                  disabled={updateMutation.isPending}
                  className="text-green-600 hover:text-green-700 disabled:opacity-50"
                >
                  {updateMutation.isPending
                    ? <Loader2 className="h-4 w-4 animate-spin" />
                    : <Save className="h-4 w-4" />}
                </button>
                <button onClick={handleCancelNameEdit} className="text-gray-400 hover:text-gray-600">
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold text-gray-900">{jd.name}</h1>
                {isLeaderOrAbove && (
                  <button
                    onClick={handleStartNameEdit}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                    aria-label={t('jd.detail.editNameAria')}
                  >
                    <Edit2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            )}

            <div className="flex items-center gap-2 flex-wrap">
              <ProcessingStatusBadge status={jd.processing_status} />
              {jd.file_name ? (
                <>
                  <span className="text-xs text-gray-400">{jd.file_name}</span>
                  {jd.file_type && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600 uppercase">
                      {jd.file_type}
                    </span>
                  )}
                  {jd.file_size != null && (
                    <span className="text-xs text-gray-400">{formatFileSize(jd.file_size)}</span>
                  )}
                </>
              ) : jd.raw_content ? (
                <span className="text-xs text-gray-400">{t('jd.detail.pastedText')}</span>
              ) : null}
              {jd.processing_status === 'processed' && currentWorkspace?.code && (
                <a
                  href={`/ats/public/careers/${currentWorkspace.code}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-orange-600 hover:text-orange-700"
                >
                  <ExternalLink className="h-3 w-3" />
                  {t('jd.detail.viewOnCareers')}
                </a>
              )}
            </div>
          </div>
        </div>

        {isLeaderOrAbove && (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleReparse}
              disabled={reparseMutation.isPending || jd.processing_status === 'processing'}
            >
              {reparseMutation.isPending
                ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                : <RefreshCw className="h-4 w-4 mr-1.5" />}
              {t('jd.detail.reparse')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowDeleteDialog(true)}
              className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
            >
              <Trash2 className="h-4 w-4 mr-1.5" />
              {t('jd.detail.delete')}
            </Button>
          </div>
        )}
      </div>

      {/* Tabs: Parsed Data | Preview JD */}
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'parsed' | 'preview')}>
        <TabsList>
          <TabsTrigger value="parsed">{t('jd.detail.tabs.parsedData')}</TabsTrigger>
          <TabsTrigger value="preview">{t('jd.detail.tabs.previewJd')}</TabsTrigger>
        </TabsList>

        {/* ── Parsed Data tab ── */}
        <TabsContent value="parsed" className="mt-4">
          <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-5">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-gray-700">{t('jd.detail.parsedTitle')}</h2>
              {isLeaderOrAbove && !editingParsedData && jd.processing_status !== 'processing' && (
                <button
                  onClick={handleStartParsedDataEdit}
                  className="flex items-center gap-1 text-xs text-orange-600 hover:text-orange-700"
                >
                  <Edit2 className="h-3 w-3" />
                  {t('jd.detail.edit')}
                </button>
              )}
            </div>

        {jd.processing_status === 'processing' ? (
          <div className="flex flex-col items-center justify-center py-12 gap-3 text-blue-600">
            <Loader2 className="h-8 w-8 animate-spin" />
            <p className="text-sm font-medium">{t('jd.detail.parsing.inProgress')}</p>
          </div>
        ) : editingParsedData ? (
          <div className="space-y-5">
            {/* Basic info */}
            <div className="space-y-3">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t('jd.detail.sections.basicInformation')}</h3>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t('jd.detail.fields.title')}</label>
                <input value={parsedDataForm.title} onChange={e => setField('title', e.target.value)}
                  className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t('jd.detail.fields.description')}</label>
                <textarea value={parsedDataForm.description} onChange={e => setField('description', e.target.value)}
                  rows={3} className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t('jd.detail.fields.employmentType')}</label>
                  <input value={parsedDataForm.employment_type} onChange={e => setField('employment_type', e.target.value)}
                    placeholder={t('jd.detail.placeholders.employmentType')} className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t('jd.detail.fields.experienceLevel')}</label>
                  <input value={parsedDataForm.experience_level} onChange={e => setField('experience_level', e.target.value)}
                    placeholder={t('jd.detail.placeholders.experienceLevel')} className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500" />
                </div>
              </div>
            </div>

            {/* Location */}
            <div className="space-y-3 border-t border-gray-100 pt-4">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t('jd.detail.sections.location')}</h3>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t('jd.detail.fields.city')}</label>
                  <input value={parsedDataForm.location_city} onChange={e => setField('location_city', e.target.value)}
                    className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t('jd.detail.fields.country')}</label>
                  <input value={parsedDataForm.location_country} onChange={e => setField('location_country', e.target.value)}
                    className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t('jd.detail.fields.address')}</label>
                <input value={parsedDataForm.location_address} onChange={e => setField('location_address', e.target.value)}
                  className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500" />
              </div>
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
                <input type="checkbox" checked={parsedDataForm.location_remote}
                  onChange={e => setField('location_remote', e.target.checked)}
                  className="rounded border-gray-300 text-orange-600 focus:ring-orange-500" />
                {t('jd.detail.fields.remotePosition')}
              </label>
            </div>

            {/* Salary */}
            <div className="space-y-3 border-t border-gray-100 pt-4">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t('jd.detail.sections.salary')}</h3>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t('jd.detail.fields.displayText')} <span className="font-normal text-gray-400">{t('jd.detail.fields.displayTextHint')}</span></label>
                <input value={parsedDataForm.salary_text} onChange={e => setField('salary_text', e.target.value)}
                  placeholder={t('jd.detail.placeholders.salaryRange')}
                  className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500" />
              </div>
              <div className="grid grid-cols-4 gap-2">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t('jd.detail.fields.min')}</label>
                  <input type="number" value={parsedDataForm.salary_min} onChange={e => setField('salary_min', e.target.value)}
                    className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t('jd.detail.fields.max')}</label>
                  <input type="number" value={parsedDataForm.salary_max} onChange={e => setField('salary_max', e.target.value)}
                    className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t('jd.detail.fields.currency')}</label>
                  <input value={parsedDataForm.salary_currency} onChange={e => setField('salary_currency', e.target.value)}
                    placeholder={t('jd.detail.placeholders.currency')} className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t('jd.detail.fields.period')}</label>
                  <input value={parsedDataForm.salary_period} onChange={e => setField('salary_period', e.target.value)}
                    placeholder={t('jd.detail.placeholders.period')} className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500" />
                </div>
              </div>
            </div>

            {/* Company */}
            <div className="space-y-3 border-t border-gray-100 pt-4">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t('jd.detail.sections.company')}</h3>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t('jd.detail.fields.name')}</label>
                  <input value={parsedDataForm.company_name} onChange={e => setField('company_name', e.target.value)}
                    className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t('jd.detail.fields.industry')}</label>
                  <input value={parsedDataForm.company_industry} onChange={e => setField('company_industry', e.target.value)}
                    className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t('jd.detail.fields.description')}</label>
                <textarea value={parsedDataForm.company_description} onChange={e => setField('company_description', e.target.value)}
                  rows={2} className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none" />
              </div>
            </div>

            {/* Lists */}
            {(
              [
                { key: 'responsibilities', labelKey: 'jd.detail.sections.responsibilities', rows: 6 },
                { key: 'qualifications', labelKey: 'jd.detail.sections.qualifications', rows: 6 },
                { key: 'skills_required', labelKey: 'jd.detail.sections.skillsRequired', rows: 4 },
                { key: 'benefits', labelKey: 'jd.detail.sections.benefits', rows: 4 },
                { key: 'tags', labelKey: 'jd.detail.sections.tags', rows: 3 },
              ] as const
            ).map(({ key, labelKey, rows }) => (
              <div key={key} className="border-t border-gray-100 pt-4">
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  {t(labelKey)} <span className="font-normal normal-case text-gray-400">{t('jd.detail.sections.onePerLine')}</span>
                </label>
                <textarea
                  value={parsedDataForm[key]}
                  onChange={e => setField(key, e.target.value)}
                  rows={rows}
                  className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none"
                />
              </div>
            ))}

            {/* Actions */}
            <div className="flex gap-2 border-t border-gray-100 pt-4">
              <Button size="sm" onClick={handleSaveParsedData} disabled={updateMutation.isPending} className="bg-orange-600 hover:bg-orange-700 text-white">
                {updateMutation.isPending && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
                {t('jd.detail.saveChanges')}
              </Button>
              <Button size="sm" variant="outline" onClick={handleCancelParsedDataEdit} disabled={updateMutation.isPending}>
                {t('jd.detail.cancel')}
              </Button>
            </div>
          </div>
        ) : (
          <>
            {jd.processing_status === 'pending' && (
              <p className="text-sm text-gray-400">{t('jd.detail.parsing.notStarted')}</p>
            )}

            {hasParseError && (
          <div className="rounded-md bg-red-50 border border-red-200 p-4 text-sm text-red-700">
            <p className="font-medium">{t('jd.detail.parsing.failed')}</p>
            <p className="mt-1 text-red-600">{parsed?.error?.message ?? t('jd.detail.parsing.errorFallback')}</p>
          </div>
        )}

        {parsed && !hasParseError && (
          <div className="space-y-5">
            {parsed.title ? (
              <div>
                <h3 className="text-lg font-bold text-gray-900">{parsed.title}</h3>
                {parsed.description ? (
                  <p className="mt-1 text-sm text-gray-600 leading-relaxed">{parsed.description}</p>
                ) : null}
              </div>
            ) : null}

            <div className="flex flex-wrap gap-2">
              {parsed.employment_type ? (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded-md text-xs font-medium">
                  <Briefcase className="h-3 w-3" />
                  {parsed.employment_type.replace(/_/g, ' ')}
                </span>
              ) : null}
              {parsed.experience_level ? (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-50 text-purple-700 rounded-md text-xs font-medium">
                  {parsed.experience_level}
                </span>
              ) : null}
              {parsed.location?.remote ? (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-50 text-green-700 rounded-md text-xs font-medium">
                  {t('jd.detail.badges.remote')}
                </span>
              ) : null}
            </div>

            {parsed.location && Object.values(parsed.location).some(Boolean) ? (
              <ParsedSection title={t('jd.detail.sections.location')} icon={<MapPin className="h-4 w-4" />}>
                <p className="text-sm text-gray-700">
                  {[parsed.location.address, parsed.location.city, parsed.location.country]
                    .filter(Boolean).join(', ') || '—'}
                </p>
              </ParsedSection>
            ) : null}

            {parsed.salary_range && Object.values(parsed.salary_range).some(Boolean) ? (
              <ParsedSection title={t('jd.detail.sections.salary')} icon={<DollarSign className="h-4 w-4" />}>
                <p className="text-sm text-gray-700">
                  {parsed.salary_range.text
                    ? String(parsed.salary_range.text)
                    : [
                        parsed.salary_range.min_amount != null && `${parsed.salary_range.min_amount}`,
                        parsed.salary_range.max_amount != null && `${parsed.salary_range.max_amount}`,
                      ].filter(Boolean).join(' – ') +
                      ` ${parsed.salary_range.currency ?? ''} / ${parsed.salary_range.period ?? ''}`}
                </p>
              </ParsedSection>
            ) : null}

            {parsed.company_info && Object.values(parsed.company_info).some(Boolean) ? (
              <ParsedSection title={t('jd.detail.sections.company')} icon={<Building2 className="h-4 w-4" />}>
                <div className="space-y-1">
                  {parsed.company_info.name ? (
                    <p className="text-sm font-medium text-gray-800">{parsed.company_info.name}</p>
                  ) : null}
                  {parsed.company_info.description ? (
                    <p className="text-sm text-gray-600">{parsed.company_info.description}</p>
                  ) : null}
                  {parsed.company_info.industry ? (
                    <p className="text-xs text-gray-500">{t('jd.detail.sections.industryPrefix', { value: parsed.company_info.industry })}</p>
                  ) : null}
                </div>
              </ParsedSection>
            ) : null}

            {parsed.responsibilities?.length ? (
              <ParsedSection title={t('jd.detail.sections.responsibilities')} icon={<CheckCircle className="h-4 w-4" />}>
                <BulletList items={parsed.responsibilities} />
              </ParsedSection>
            ) : null}

            {parsed.qualifications?.length ? (
              <ParsedSection title={t('jd.detail.sections.qualifications')} icon={<CheckCircle className="h-4 w-4" />}>
                <BulletList items={parsed.qualifications} />
              </ParsedSection>
            ) : null}

            {parsed.skills_required?.length ? (
              <ParsedSection title={t('jd.detail.sections.skillsRequired')} icon={<Tag className="h-4 w-4" />}>
                <TagList items={parsed.skills_required} />
              </ParsedSection>
            ) : null}

            {parsed.benefits?.length ? (
              <ParsedSection title={t('jd.detail.sections.benefits')} icon={<CheckCircle className="h-4 w-4" />}>
                <BulletList items={parsed.benefits} />
              </ParsedSection>
            ) : null}

            {parsed.tags?.length ? (
              <ParsedSection title={t('jd.detail.sections.tags')} icon={<Tag className="h-4 w-4" />}>
                <TagList items={parsed.tags} />
              </ParsedSection>
            ) : null}
          </div>
        )}

            {jd.processing_status === 'processed' && !parsed && (
              <p className="text-sm text-gray-400">{t('jd.detail.parsing.noData')}</p>
            )}
          </>
        )}
          </div>
        </TabsContent>

        {/* ── Preview JD tab ── */}
        <TabsContent value="preview" className="mt-4">
          {editingFileUrl ? (
            /* Replace file mode */
            <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
              {jd.file_url && (
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <FileText className="h-3.5 w-3.5 text-gray-400" />
                  <span>{t('jd.detail.preview.currentPrefix', { name: jd.file_name ?? extractFilename(jd.file_url) })}</span>
                  {jd.file_size != null && <span className="text-gray-400">({formatFileSize(jd.file_size)})</span>}
                </div>
              )}
              {pendingFile ? (
                <div className="flex items-center gap-2 px-3 py-2.5 border border-green-200 bg-green-50 rounded-md">
                  <FileText className="h-4 w-4 text-green-600 flex-shrink-0" />
                  <span className="text-sm text-green-700 flex-1 truncate">{pendingFile.name}</span>
                  <button
                    type="button"
                    onClick={() => { setPendingFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                    className="text-green-600 hover:text-green-800"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full flex flex-col items-center justify-center gap-2 px-4 py-8 border-2 border-dashed border-gray-300 rounded-md hover:border-orange-400 hover:bg-orange-50 transition-colors"
                >
                  <Upload className="h-7 w-7 text-gray-400" />
                  <span className="text-sm text-gray-500">
                    <span className="text-orange-600 font-medium">{t('jd.detail.preview.clickToSelect')}</span>{t('jd.detail.preview.newFileSuffix')}
                  </span>
                  <span className="text-xs text-gray-400">{t('jd.detail.preview.supportedFormats')}</span>
                </button>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.docx,.txt"
                onChange={e => setPendingFile(e.target.files?.[0] ?? null)}
                className="hidden"
              />
              <p className="text-xs text-amber-600 bg-amber-50 border border-amber-100 rounded-md px-3 py-2">
                {t('jd.detail.preview.replaceWarning')}
              </p>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={handleSaveNewFile}
                  disabled={!pendingFile || uploadFileMutation.isPending}
                  className="bg-orange-600 hover:bg-orange-700 text-white"
                >
                  {uploadFileMutation.isPending && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
                  {t('jd.detail.saveAndUpdate')}
                </Button>
                <Button size="sm" variant="outline" onClick={handleCancelFileEdit} disabled={uploadFileMutation.isPending}>
                  {t('jd.detail.cancel')}
                </Button>
              </div>
            </div>
          ) : jd.file_url ? (
            /* File preview */
            <>
              {isLeaderOrAbove && (
                <div className="flex justify-end mb-2">
                  <button
                    onClick={handleStartFileUrlEdit}
                    disabled={jd.processing_status === 'processing'}
                    className="flex items-center gap-1 text-xs text-orange-600 hover:text-orange-700 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Edit2 className="h-3 w-3" />
                    {t('jd.detail.replaceFile')}
                  </button>
                </div>
              )}
              {fileUrlQuery.isLoading ? (
                <div className="flex items-center justify-center h-[800px] bg-gray-50 rounded-lg border border-gray-200">
                  <Loader2 className="h-8 w-8 text-gray-400 animate-spin" />
                </div>
              ) : fileUrlQuery.isError ? (
                <div className="flex items-center justify-center h-40 bg-red-50 rounded-lg border border-red-200 text-sm text-red-500">
                  {t('jd.detail.preview.loadFailed')}
                </div>
              ) : fileUrlQuery.data?.url ? (
                isPDF(jd.file_type ?? jd.file_url) ? (
                  <div className="h-[800px]">
                    <PDFViewer url={fileUrlQuery.data.url} fileName={jd.file_name ?? extractFilename(jd.file_url)} />
                  </div>
                ) : (
                  <div className="flex items-center gap-2 px-4 py-6 bg-white border border-gray-200 rounded-lg text-sm text-gray-600">
                    <FileText className="h-4 w-4 text-gray-400" />
                    <a href={fileUrlQuery.data.url} target="_blank" rel="noopener noreferrer" className="text-orange-600 hover:underline">
                      {jd.file_name ?? t('jd.detail.preview.openInNewTab')}
                    </a>
                  </div>
                )
              ) : null}
            </>
          ) : jd.raw_content ? (
            /* Raw content */
            <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
              {isLeaderOrAbove && !editingRawContent && jd.processing_status !== 'processing' && (
                <div className="flex justify-end">
                  <button
                    onClick={handleStartRawContentEdit}
                    className="flex items-center gap-1 text-xs text-orange-600 hover:text-orange-700"
                  >
                    <Edit2 className="h-3 w-3" />
                    {t('jd.detail.edit')}
                  </button>
                </div>
              )}
              {editingRawContent ? (
                <div className="space-y-3">
                  <textarea
                    value={rawContentValue}
                    onChange={e => setRawContentValue(e.target.value)}
                    rows={20}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 font-sans resize-y"
                  />
                  <div className="flex gap-2">
                    <Button size="sm" onClick={handleSaveRawContent} disabled={updateMutation.isPending} className="bg-orange-600 hover:bg-orange-700 text-white">
                      {updateMutation.isPending && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
                      {t('jd.detail.save')}
                    </Button>
                    <Button size="sm" variant="outline" onClick={handleCancelRawContentEdit} disabled={updateMutation.isPending}>
                      {t('jd.detail.cancel')}
                    </Button>
                  </div>
                </div>
              ) : (
                <pre className="text-sm text-gray-700 whitespace-pre-wrap font-sans leading-relaxed">
                  {jd.raw_content}
                </pre>
              )}
            </div>
          ) : (
            /* No content */
            <div className="flex flex-col items-center justify-center h-40 bg-gray-50 rounded-lg border border-gray-200 text-sm text-gray-400">
              <FileText className="h-8 w-8 mb-2 text-gray-300" />
              {t('jd.detail.preview.noFileAttached')}
              {isLeaderOrAbove && jd.processing_status !== 'processing' && (
                <button onClick={handleStartFileUrlEdit} className="mt-2 text-orange-600 hover:text-orange-700 text-xs">
                  {t('jd.detail.preview.uploadFile')}
                </button>
              )}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Delete dialog */}
      <AlertDialog
        open={showDeleteDialog}
        onOpenChange={(open) => { if (!deleteMutation.isPending) setShowDeleteDialog(open); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('jd.detail.deleteDialog.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('jd.detail.deleteDialog.body', { name: jd.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>{t('jd.detail.deleteDialog.cancel')}</AlertDialogCancel>
            <Button
              onClick={(e) => { e.preventDefault(); handleDeleteConfirm(); }}
              disabled={deleteMutation.isPending}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleteMutation.isPending ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t('jd.detail.deleteDialog.deleting')}</>
              ) : t('jd.detail.deleteDialog.confirm')}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
